import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { backfillVisualizationHistory } from "../lib/visualization-history";
import type { WorldTerm } from "../lib/world";
import { latestArcDurationForTick } from "../lib/timing";
import type {
  RecamanTermInput,
  RecamanHistoryTermInput,
  RecamanVisualization,
  RecamanVisualizationSnapshot,
  VisualizationViewMode,
} from "../visualization";

export interface VisualizationHandle {
  follow: () => void;
  overview: () => void;
  panLeft: () => void;
  panRight: () => void;
  panUp: () => void;
  panDown: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  center: () => void;
}

const KEYBOARD_PAN_STEP_PX = 72;
const HISTORY_INCREMENT_THRESHOLD = 2_048n;
const HISTORY_SYNC_CHECK_MS = 1_000;
const HISTORY_RETRY_DELAY_MS = 5_000;

interface VisualizationCanvasProps {
  terms: readonly WorldTerm[];
  tickMs: number;
  mode: VisualizationViewMode;
  reducedMotion: boolean;
  historyIdentity: string;
  historyThrough: string;
  onViewChange: (snapshot: RecamanVisualizationSnapshot) => void;
}

function toFiniteNumber(value: string): number {
  const result = Number(BigInt(value));
  return Number.isFinite(result) ? result : Number.MAX_VALUE;
}

function toVisualizationTerms(terms: readonly WorldTerm[]): RecamanTermInput[] {
  return terms.map((term) => ({
    index: toFiniteNumber(term.n),
    value: toFiniteNumber(term.value),
    step: toFiniteNumber(term.step),
    operation: term.operation,
  }));
}

function toVisualizationHistoryTerms(terms: readonly WorldTerm[]): RecamanHistoryTermInput[] {
  return terms.map((term) => ({
    index: toFiniteNumber(term.n),
    previousValue: toFiniteNumber(term.previous),
    value: toFiniteNumber(term.value),
    step: toFiniteNumber(term.step),
    operation: term.operation,
  }));
}

export const VisualizationCanvas = forwardRef<VisualizationHandle, VisualizationCanvasProps>(
  function VisualizationCanvas({
    terms,
    tickMs,
    mode,
    reducedMotion,
    historyIdentity,
    historyThrough,
    onViewChange,
  }, ref) {
    const hostRef = useRef<HTMLDivElement>(null);
    const visualizationRef = useRef<RecamanVisualization | null>(null);
    const historyThroughRef = useRef(historyThrough);
    const [rendererReady, setRendererReady] = useState(false);
    historyThroughRef.current = historyThrough;
    const onViewChangeRef = useRef(onViewChange);
    onViewChangeRef.current = onViewChange;
    const visualTerms = useMemo(() => toVisualizationTerms(terms), [terms]);
    const latestArcDurationMs = latestArcDurationForTick(tickMs);
    const latestStateRef = useRef({
      visualTerms,
      mode,
      reducedMotion,
      latestArcDurationMs,
    });
    latestStateRef.current = {
      visualTerms,
      mode,
      reducedMotion,
      latestArcDurationMs,
    };

    useImperativeHandle(ref, () => ({
      follow: () => visualizationRef.current?.follow(),
      overview: () => visualizationRef.current?.overview(),
      panLeft: () => visualizationRef.current?.panBy(KEYBOARD_PAN_STEP_PX, 0),
      panRight: () => visualizationRef.current?.panBy(-KEYBOARD_PAN_STEP_PX, 0),
      panUp: () => visualizationRef.current?.panBy(0, -KEYBOARD_PAN_STEP_PX),
      panDown: () => visualizationRef.current?.panBy(0, KEYBOARD_PAN_STEP_PX),
      zoomIn: () => visualizationRef.current?.zoomAt(1.35),
      zoomOut: () => visualizationRef.current?.zoomAt(1 / 1.35),
      center: () => visualizationRef.current?.follow(),
    }), []);

    useEffect(() => {
      const host = hostRef.current;
      if (!host) return;
      let disposed = false;
      let instance: RecamanVisualization | null = null;

      void import("../visualization").then(async ({ createRecamanVisualization }) => {
        if (disposed) return;
        const initialState = latestStateRef.current;
        instance = await createRecamanVisualization({
          host,
          terms: initialState.visualTerms,
          initialMode: initialState.mode,
          latestArcDurationMs: initialState.latestArcDurationMs,
          maxVisibleArcs: 8_192,
          reducedMotion: initialState.reducedMotion,
          onViewChange: (snapshot) => onViewChangeRef.current(snapshot),
        });
        if (disposed) {
          instance.destroy();
          return;
        }
        visualizationRef.current = instance;
        setRendererReady(true);
        const latestState = latestStateRef.current;
        instance.update({
          terms: latestState.visualTerms,
          mode: latestState.mode,
          reducedMotion: latestState.reducedMotion,
          animateLatest: !latestState.reducedMotion,
          latestArcDurationMs: latestState.latestArcDurationMs,
        });
        instance.render();
      }).catch((error: unknown) => {
        console.error("visualization_init_failed", { error: error instanceof Error ? error.message : String(error) });
      });

      return () => {
        disposed = true;
        setRendererReady(false);
        visualizationRef.current = null;
        instance?.destroy();
      };
      // The renderer owns subsequent updates; initialization is intentionally once per host.
    }, []);

    useEffect(() => {
      visualizationRef.current?.update({
        terms: visualTerms,
        mode,
        reducedMotion,
        animateLatest: !reducedMotion,
        latestArcDurationMs,
      });
    }, [latestArcDurationMs, mode, reducedMotion, visualTerms]);

    useEffect(() => {
      if (!rendererReady) return;
      const instance = visualizationRef.current;
      const host = hostRef.current;
      if (!instance || !host) return;

      const controller = new AbortController();
      const { signal } = controller;
      let loadedThrough = -1n;
      let loadedChunks = 0n;
      let pendingThrough = -1n;
      instance.clearHistory();
      host.dataset.historyIdentity = historyIdentity;
      host.dataset.historyState = "loading";
      host.dataset.historyLoaded = "0";
      host.dataset.historyArcs = "0";
      host.dataset.historyTotal = (BigInt(historyThroughRef.current) + 1n).toString();

      const loadRange = async (from: bigint, through: bigint): Promise<void> => {
        const loadedBefore = loadedThrough + 1n;
        const chunksBefore = loadedChunks;
        const result = await backfillVisualizationHistory({
          from,
          through,
          signal,
          onChunk: ({ terms: chunkTerms }) => {
            if (signal.aborted) return;
            instance.ingestHistory(toVisualizationHistoryTerms(chunkTerms));
            host.dataset.historyArcs = instance.getSnapshot().historyArcCount.toString();
          },
          onProgress: (progress) => {
            if (signal.aborted) return;
            host.dataset.historyLoaded = (loadedBefore + progress.loadedTerms).toString();
            host.dataset.historyTotal = (through + 1n).toString();
            host.dataset.historyChunks = (chunksBefore + progress.completedChunks).toString();
          },
        });
        loadedThrough = through;
        loadedChunks += result.completedChunks;
        host.dataset.historyLoaded = (loadedThrough + 1n).toString();
        host.dataset.historyTotal = (loadedThrough + 1n).toString();
      };

      const maintainHistory = async (): Promise<void> => {
        while (!signal.aborted) {
          const latestThrough = BigInt(historyThroughRef.current);
          const missingTerms = latestThrough - loadedThrough;
          if (
            loadedThrough >= 0n
            && missingTerms < HISTORY_INCREMENT_THRESHOLD
          ) {
            await waitForHistoryWake(signal, HISTORY_SYNC_CHECK_MS, false);
            continue;
          }

          if (pendingThrough <= loadedThrough) pendingThrough = latestThrough;

          host.dataset.historyState = "loading";
          delete host.dataset.historyError;
          try {
            await loadRange(loadedThrough + 1n, pendingThrough);
            if (signal.aborted) return;
            pendingThrough = -1n;
            host.dataset.historyState = "complete";
          } catch (error: unknown) {
            if (signal.aborted) return;
            const message = error instanceof Error ? error.message : String(error);
            host.dataset.historyState = "error";
            host.dataset.historyError = message;
            console.warn("visualization_history_load_failed", { error: message });
            await waitForHistoryWake(signal, HISTORY_RETRY_DELAY_MS, true);
          }
        }
      };

      void maintainHistory().catch((error: unknown) => {
        if (signal.aborted) return;
        const message = error instanceof Error ? error.message : String(error);
        host.dataset.historyState = "error";
        host.dataset.historyError = message;
        console.warn("visualization_history_sync_failed", { error: message });
      });

      return () => controller.abort();
    }, [historyIdentity, rendererReady]);

    return <div ref={hostRef} className="visualization-shell" aria-hidden="true" />;
  },
);

function waitForHistoryWake(
  signal: AbortSignal,
  delayMs: number,
  wakeOnOnline: boolean,
): Promise<void> {
  if (signal.aborted) return Promise.reject(signal.reason);
  return new Promise((resolve, reject) => {
    const finish = (): void => {
      clearTimeout(timer);
      signal.removeEventListener("abort", abort);
      if (wakeOnOnline) window.removeEventListener("online", finish);
      resolve();
    };
    const abort = (): void => {
      clearTimeout(timer);
      if (wakeOnOnline) window.removeEventListener("online", finish);
      reject(signal.reason ?? new DOMException("The operation was aborted", "AbortError"));
    };
    const timer = window.setTimeout(finish, delayMs);
    signal.addEventListener("abort", abort, { once: true });
    if (wakeOnOnline) window.addEventListener("online", finish, { once: true });
  });
}
